export const INSIGHT_CATEGORIES = ["All", "Strategy", "Operations", "Digital", "ESG", "Risk", "Human Capital"];

export const INSIGHTS = [
  {
    slug: "boardroom-clarity-in-uncertain-markets",
    title: "Boardroom clarity in uncertain markets",
    excerpt: "Four moves CEOs are making to convert macro volatility into operating leverage — and why most leadership teams hesitate too long.",
    category: "Strategy",
    date: "2026-02-08",
    readTime: 7,
    author: "Worldly Solutions Strategy Practice",
    cover: "https://images.unsplash.com/photo-1758691736493-aa6d22c0f8a6?w=1400&q=80",
    content: [
      "Markets reward decisiveness — but only when paired with discipline. The last 18 months have produced a generation of executives schooled in shock: supply-chain disruption, capital tightening, and an AI inflection that has compressed planning horizons from quarters into weeks. The instinct to wait for clarity is understandable. It is also expensive.",
      "Our work with growth-stage and mid-market enterprises across 21 industries surfaces four moves that consistently separate the boards that compound advantage from those that merely survive a cycle.",
      "First, narrow the strategy to no more than three time-bound bets. Boards that try to defend every position end up defending none. Second, instrument the bets — every bet needs a leading indicator visible at board cadence, not a lagging one revealed at year-end. Third, decouple the operating model from the bets. The temptation to re-org around strategy is strong; it is also the fastest way to lose the institutional memory that makes execution possible. Fourth, write the post-mortem in advance — articulate, in writing, what would cause you to kill each bet at 90, 180, and 365 days.",
      "None of this is novel. What is novel is the willingness to apply it under pressure. The boards that do are not smarter — they are simply more comfortable being wrong in small, recoverable ways."
    ]
  },
  {
    slug: "operational-excellence-second-curve",
    title: "The operational excellence second curve",
    excerpt: "Lean is necessary but no longer sufficient. The next decade of operating advantage will be won on data-native processes.",
    category: "Operations",
    date: "2026-01-28",
    readTime: 6,
    author: "Worldly Solutions Operations Practice",
    cover: "https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=1400&q=80",
    content: [
      "Three decades of Lean and Six Sigma have produced organizations that are demonstrably better at reducing variation. They have not, however, produced organizations that are notably faster at learning. That is the second curve.",
      "Data-native process design treats every workflow as an experiment with an instrument attached. The cost of the instrumentation has collapsed; the cost of not having it has not.",
      "Our practical heuristic: if the process owner cannot describe — in three sentences, without slides — the leading indicator, the lagging indicator, and the loop that connects them, the process is not yet data-native. Most operating reviews would benefit from cutting their deck by 80% and adding that test."
    ]
  },
  {
    slug: "ai-readiness-without-the-theatre",
    title: "AI readiness without the theatre",
    excerpt: "A diagnostic-first approach to enterprise AI that produces compounding capability — not a graveyard of pilots.",
    category: "Digital",
    date: "2026-01-15",
    readTime: 8,
    author: "Worldly Solutions Digital Practice",
    cover: "https://images.unsplash.com/photo-1518770660439-4636190af475?w=1400&q=80",
    content: [
      "Most enterprise AI programs are over-engineered at the model layer and under-engineered everywhere else. The result is a portfolio of impressive demos that never reach production.",
      "Our Digital Transformation Assessment surfaces eight capability dimensions: AI readiness, automation, digital processes, ERP maturity, analytics, cloud adoption, cybersecurity, and technology strategy. Organizations that score above 70% on at least six of these dimensions ship AI to production at roughly 4x the rate of organizations that score above 85% on AI readiness alone.",
      "The lesson is unromantic: AI is a downstream beneficiary of upstream hygiene. Fund the hygiene first."
    ]
  },
  {
    slug: "esg-reporting-readiness-2026",
    title: "ESG reporting readiness: what 2026 demands",
    excerpt: "BRSR Core, CSRD, and the new wave of disclosure standards — a pragmatic readiness checklist for Indian and global enterprises.",
    category: "ESG",
    date: "2026-01-03",
    readTime: 9,
    author: "Worldly Solutions ESG Practice",
    cover: "https://images.unsplash.com/photo-1466611653911-95081537e5b7?w=1400&q=80",
    content: [
      "The next two reporting cycles will separate the organizations that treat ESG as a disclosure obligation from those that treat it as an operating discipline. The disclosure-only path is increasingly expensive — both in audit fees and in capital-market discounting.",
      "Our ESG readiness diagnostic surfaces eight dimensions including reporting readiness, carbon footprint accuracy, and ESG-risk integration into enterprise risk frameworks. The single highest-leverage move is not a new report — it is a single source of ESG data that already serves both the disclosure and the operating use cases."
    ]
  },
  {
    slug: "enterprise-risk-the-new-tail",
    title: "Enterprise risk: the new tail",
    excerpt: "Cyber, geopolitical, and AI-related risks are no longer tail events. A reframed enterprise risk operating model for 2026.",
    category: "Risk",
    date: "2025-12-18",
    readTime: 6,
    author: "Worldly Solutions Risk Practice",
    cover: "https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=1400&q=80",
    content: [
      "Traditional enterprise risk frameworks treat low-probability, high-impact events as a separate category — the tail. The defining shift of the last decade is that the tail is no longer thin.",
      "A modern enterprise risk operating model integrates cyber, supply chain, geopolitical, AI-model, and reputational risks into the same cadence as financial and operational risk — owned by named executives, instrumented with leading indicators, and stress-tested at board level."
    ]
  },
  {
    slug: "talent-pipeline-leaders-build",
    title: "The talent pipeline that compounding leaders build",
    excerpt: "Succession planning is not an HR exercise. It is the single most-cited gap in our Human Capital Assessment.",
    category: "Human Capital",
    date: "2025-12-04",
    readTime: 5,
    author: "Worldly Solutions Human Capital Practice",
    cover: "https://images.unsplash.com/photo-1521737604893-d14cc237f11d?w=1400&q=80",
    content: [
      "Across hundreds of Human Capital assessments, the dimension that most consistently scores below the rest is succession planning. Even organizations that score 'Leader' in culture and engagement frequently score 'Emerging' in named successors for critical roles.",
      "The fix is not a talent review meeting. It is a structural change: every executive's KPIs should include a named successor with a 24-month readiness plan, refreshed quarterly, with a written rationale for why each successor is — or is not yet — ready."
    ]
  },
  {
    slug: "growth-readiness-the-90-day-window",
    title: "Growth readiness: the 90-day window",
    excerpt: "What separates organizations that compound from those that stall — and why the next 90 days matter more than the next 18 months.",
    category: "Strategy",
    date: "2025-11-20",
    readTime: 7,
    author: "Worldly Solutions Growth Practice",
    cover: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=1400&q=80",
    content: [
      "Our Business Growth Readiness Index combines ten capability modules into a single executive scorecard. The data tells a consistent story: organizations that move from 'Developing' to 'Advanced' do so in compressed 90-day windows — not gradual multi-year arcs.",
      "The window typically opens after an external shock or a leadership change and closes when the next quarterly cycle reasserts itself. Boards that recognize the window and pre-stage three to five disproportionate moves capture a multi-year advantage. Boards that wait for consensus do not."
    ]
  }
];

export const getInsight = (slug) => INSIGHTS.find(i => i.slug === slug);
